import * as Yup from "yup";
import { VALIDATION_ENUM } from "enums";

// ─── Wspólne reguły walidacji ────────────────────────────────────────────────

const requiredString = Yup.string().required(VALIDATION_ENUM.REQUIRED);

const requiredPositiveNumber = Yup.string()
  .required(VALIDATION_ENUM.REQUIRED)
  .test("is-positive", VALIDATION_ENUM.MIN_1, (val) => {
    if (!val) return false;
    return Number(val) > 0;
  });

const optionalPositiveNumber = Yup.string()
  .notRequired()
  .test("is-positive-if-filled", VALIDATION_ENUM.MIN_1, (val) => {
    if (!val) return true;
    return Number(val) > 0;
  });

const optionalString = Yup.string().notRequired();

const requiredNonNegativeNumberMax999 = Yup.string()
  .required(VALIDATION_ENUM.REQUIRED)
  .test("is-non-negative", VALIDATION_ENUM.MIN_1, (val) => {
    if (val === null || val === undefined || val === "") return false;
    return !isNaN(Number(val)) && Number(val) >= 0;
  })
  .test("is-max-999.99", "Wartość nie może być większa niż 999.99", (val) => {
    if (!val) return true;
    return Number(val) <= 999.99;
  });


const requiredMinPointOne = Yup.string()
  .required(VALIDATION_ENUM.REQUIRED)
  .test("is-min-0.01", "Minimalna wartość to 0.01", (val) => {
    if (!val) return false;
    return Number(val) >= 0.01;
  });

const baseSchema = {
  primaryValue: requiredPositiveNumber,
  additionalInfo: optionalString,
  relianceCanView: Yup.boolean(),
};


const stocksSchema = Yup.object().shape({
  ...baseSchema,
  quantity: requiredPositiveNumber,
  purchaseRate: requiredPositiveNumber,
});

const sharesSchema = Yup.object().shape({
  ...baseSchema,
  quantity: requiredPositiveNumber,
  purchaseRate: requiredPositiveNumber,
});

const realEstateSchema = Yup.object().shape({
  ...baseSchema,
  propertyType: requiredString,
  city: requiredString,
  address: requiredString,
  area: optionalPositiveNumber,
  propertyTypeOther: Yup.string().when("propertyType", {
    is: "other",
    then: (schema) => schema.required(VALIDATION_ENUM.REQUIRED),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const cryptoSchema = Yup.object().shape({
  ...baseSchema,
  primaryValue: optionalPositiveNumber,
  quantity: requiredMinPointOne,
  name: requiredString,
  purchaseDate: requiredString,
});

const preciousStonesSchema = Yup.object().shape({
  ...baseSchema,
  currencyUuid: requiredString,
  mass: optionalPositiveNumber,
  cut: optionalString,
  color: optionalString,
  clarity: optionalString,
  name: requiredString,
});

const optionalFiness = Yup.string()
  .notRequired()
  .test("finess-format", "Nieprawidłowy format próby", (val) => {
    if (!val) return true;
    if (val.endsWith("%")) return /^\d+(\.\d{1})?%$/.test(val);
    return /^\d+$/.test(val);
  });

const preciousMetalsSchema = Yup.object().shape({
  ...baseSchema,
  currencyUuid: requiredString,
  propertyType: requiredString,
  mass: optionalPositiveNumber,
  finess: optionalFiness,
  name: Yup.string().when("propertyType", {
    is: "other",
    then: (schema) => schema.required(VALIDATION_ENUM.REQUIRED),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const treasuryBondsSchema = Yup.object().shape({
  ...baseSchema,
  currencyUuid: requiredString,
  quantity: Yup.string()
    .required(VALIDATION_ENUM.REQUIRED)
    .test("is-integer-min-1", "Minimalna wartość to 1, tylko liczby całkowite", (val) => {
      if (!val) return false;
      const num = Number(val);
      return Number.isInteger(num) && num >= 1;
    }),
  interestRate: Yup.string()
    .required(VALIDATION_ENUM.REQUIRED)
    .test("is-min-0.01", "Minimalna wartość to 0.01", (val) => {
      if (!val) return false;
      return Number(val) >= 0.01;
    })
    .test("is-max-100", "Maksymalna wartość to 100", (val) => {
      if (!val) return true;
      return Number(val) <= 100;
    }),
});

const artworkSchema = Yup.object().shape({
  ...baseSchema,
  author: Yup.string().when("unknownAuthor", {
    is: true,
    then: (schema) => schema.notRequired(),
    otherwise: (schema) => schema.required(VALIDATION_ENUM.REQUIRED),
  }),
  artworkType: requiredString,
  currencyUuid: requiredString,
});

const investmentPolicySchema = Yup.object().shape({
  ...baseSchema,
  name: requiredString,
  currencyUuid: requiredString,
  primaryValue: optionalFiness,
  durationDateValue: Yup.string().when("durationDate", {
    is: "specific_date",
    then: (schema) => schema.required(VALIDATION_ENUM.REQUIRED),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const loansSchema = Yup.object().shape({
  ...baseSchema,
  entity: requiredString,
  currencyUuid: requiredString,
  interestRate: requiredNonNegativeNumberMax999,
  durationDateValue: Yup.string().when("durationDate", {
    is: "specific_date",
    then: (schema) => schema.required(VALIDATION_ENUM.REQUIRED),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const currenciesSchema = Yup.object().shape({
  ...baseSchema,
  currencyUuid: requiredString,
  purchaseDate: requiredString,
});

const otherSchema = Yup.object().shape({
  ...baseSchema,
  name: requiredString,
});


export const validationSchemas: Record<string, Yup.ObjectSchema<any>> = {
  stocks: stocksSchema,
  shares: sharesSchema,
  real_estate: realEstateSchema,
  crypto: cryptoSchema,
  precious_stones: preciousStonesSchema,
  precious_metals: preciousMetalsSchema,
  treasury_bonds: treasuryBondsSchema,
  artwork: artworkSchema,
  investment_policy: investmentPolicySchema,
  loans: loansSchema,
  currencies: currenciesSchema,
  other: otherSchema,
};

export const getValidationSchema = (slug: string) => {
  return validationSchemas[slug] || otherSchema;
};
